import React, { useEffect } from "react";
import MainContainer from "components/MainContainer";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree from "./StepThree";
import { AccountDetails, BuyFlowUser } from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import {
  fetchAddMemberPlanPackages,
  fetchSignupCharge,
  resetBuyFlow,
} from "features/slices/buyFlowSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import fileServices from "services/file.services";
import toast from "utils/toast";
import FullScreenSpinner from "components/FullScreenSpinner";
import { useIsFocused } from "@react-navigation/native";
import { View } from "react-native";
import { AccountStackParamList } from "navigation/types";
import { useDebouncedCallback } from "use-debounce";
import { planPackageIdArray } from "screens/Register/registerUtils";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import useTenantUserFields from "hooks/useTenantUserFields";
import { useAccount } from "hooks/useAccount";
import { useMutation } from "@tanstack/react-query";
import { addMembersFetcher } from "queries/account.queries";
import usePlanPackageUserFields from "hooks/usePlanPackageUserFields";
import useSendConversionData from "hooks/useSendConversionData";
import Stepper, { StepperContent } from "components/Stepper/Stepper";
import { resetAddMembershipFlow } from "features/slices/addMembershipFlowSlice";
import { convertUserToUserFields } from "utils/userUtils";

const AddMembership = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<AccountStackParamList>>();
  const { account, updateFetchAccountBffCache } = useAccount();

  const isFocused = useIsFocused();
  const dispatch = useAppDispatch();
  const {
    planPackages,
    step,
    signupCharge,
    selectedBillingInterval,
    selectedPlanPackage,
  } = useAppSelector((state) => state.buyFlow);
  const { adultMemberQuantity, childMemberQuantity, newMembers } =
    useAppSelector((state) => state.addMembershipFlow);
  const { tenantUserFields } = useTenantUserFields();
  const { planPackageUserFields } = usePlanPackageUserFields(
    selectedPlanPackage?.id
  );

  const sendConversionData = useSendConversionData();
  const { mutateAsync: addMembers, isLoading: loading } =
    useMutation(addMembersFetcher);

  const goBack = () => {
    if (!isFocused) return;
    navigation.replace("ManageSubscriptions", {
      screen: "ManageSubscriptionsScreen",
    });
  };

  const initPlanPackages = async (account: AccountDetails) => {
    dispatch(fetchAddMemberPlanPackages(account));
  };

  const refetchSignupCharge = useDebouncedCallback((selectedPlanPackage) => {
    if (adultMemberQuantity || childMemberQuantity) {
      dispatch(
        fetchSignupCharge({
          planPackageIds: planPackageIdArray(
            adultMemberQuantity + childMemberQuantity,
            selectedPlanPackage.id
          ),
          billingInterval: selectedBillingInterval,
        })
      );
    }
  }, 250);

  const getImageIdentifier = async (imageIdentifier: string) => {
    if (imageIdentifier === "") return "";
    const response = await fileServices.uploadImage(imageIdentifier);
    return response.value;
  };

  const uploadImage = async (membersParam: any) => {
    for (let member of membersParam) {
      member.imageIdentifier = await getImageIdentifier(member.imageIdentifier);
    }
  };

  const addMemberToAccount = async (members: BuyFlowUser[]) => {
    const membersParam = members.map((member) => ({
      ...member,
      planPackageIds: [selectedPlanPackage!.id],
      imageIdentifier: member.imageIdentifier,
      userFields: {
        ...convertUserToUserFields({
          user: member,
          tenantOrPlanPackageUserFields: tenantUserFields!,
        }),
        ...convertUserToUserFields({
          user: member,
          tenantOrPlanPackageUserFields: planPackageUserFields!,
        }),
      },
      type: "SECONDARY",
    }));
    // retreive image identifier for each member
    await uploadImage(membersParam);
    return await addMembers({
      billingInterval: selectedBillingInterval,
      members: membersParam as any,
      accountId: account!.id,
      source: "FANRALLY_APP",
    });
  };

  const handleSubmit = async () => {
    const totalQuantity = adultMemberQuantity + childMemberQuantity;
    const updatedAccount = await addMemberToAccount([...newMembers]);
    goBack();
    updateFetchAccountBffCache(updatedAccount);
    dispatch(resetBuyFlow());
    dispatch(resetAddMembershipFlow());
    toast.success("Membership added");
    sendConversionData(signupCharge, totalQuantity);
  };

  useEffect(() => {
    if (!account || !isFocused) {
      return;
    }
    initPlanPackages(account);
  }, []);

  useEffect(() => {
    if (!selectedPlanPackage) {
      return;
    }
    refetchSignupCharge(selectedPlanPackage);
  }, [
    selectedPlanPackage,
    selectedBillingInterval,
    adultMemberQuantity,
    childMemberQuantity,
  ]);

  useEffect(() => {
    navigation.addListener("beforeRemove", () => {
      dispatch(resetBuyFlow());
    });
  }, []);

  if (!account) return null;

  if (loading)
    return (
      <FullScreenSpinner text="Adding membership to your account, please wait..."></FullScreenSpinner>
    );

  return (
    <MainContainer title="Add Membership" goBackAction={goBack} grayBackground>
      <View style={{ padding: 15 }}>
        <Stepper
          step={step}
          steps={[
            { name: "Choose New Member's Plan" },
            { name: "Add Member Details" },
            { name: "Review" },
          ]}
        >
          <StepperContent>
            <StepOne planPackages={planPackages} />
          </StepperContent>
          <StepperContent>
            <StepTwo></StepTwo>
          </StepperContent>
          <StepperContent>
            {signupCharge && selectedPlanPackage && (
              <StepThree onSubmit={handleSubmit} />
            )}
          </StepperContent>
        </Stepper>
      </View>
    </MainContainer>
  );
};

export default AddMembership;
